import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Navbar from "../components/Navbar";
import "./css/CommonComplete.css";

import pendingImage from "../assets/images/pending.png";
import successImage from "../assets/images/success.png";
import rejectedImage from "../assets/images/rejected.png";

export default function ProfileStep4() {
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState(null); // 'pending', 'success', 'rejected'
  const [rejectionNote, setRejectionNote] = useState("");
  const [rejectedStep, setRejectedStep] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      step: "Step",
      verificationPending: "Verification Pending",
      verificationCompleted: "Verification Completed",
      verificationFailed: "Verification Failed",
      pendingMessage: "Sit back and relax as we complete the verification process.",
      successMessage: "We have successfully verified your details, now you can go ahead and complete the other steps.",
      failedMessage: "We have found some information mismatch in the details that you have provided, please correct those to continue.",
      failedInfo: "Failed Info",
      refreshStatus: "Refresh Status",
      continue: "Continue",
      goToErrorPage: "Go To Error Page",
      failedToLoad: "Failed to load profile data",
      somethingWrong: "Something went wrong!",
    },
    ar: {
      loading: "جاري التحميل",
      step: "الخطوة",
      verificationPending: "التحقق معلق",
      verificationCompleted: "اكتمل التحقق",
      verificationFailed: "فشل التحقق",
      pendingMessage: "اجلس واسترخ بينما نكمل عملية التحقق.",
      successMessage: "لقد قمنا بالتحقق من تفاصيلك بنجاح، يمكنك الآن المتابعة وإكمال الخطوات الأخرى.",
      failedMessage: "لقد وجدنا بعض عدم تطابق في المعلومات التي قدمتها، يرجى تصحيحها للمتابعة.",
      failedInfo: "معلومات الفشل",
      refreshStatus: "تحديث الحالة",
      continue: "متابعة",
      goToErrorPage: "انتقل إلى صفحة الخطأ",
      failedToLoad: "فشل تحميل بيانات الملف الشخصي",
      somethingWrong: "حدث خطأ ما!",
    },
  }[lang];

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setInitialLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profile = data.response;

          // Set language preference from API response to localStorage
          if (profile.language_preference) {
            localStorage.setItem("app_language", profile.language_preference);
            setLang(profile.language_preference);
          }

          // Navigation logic based on profile_completed_step
          const completedStep = profile.profile_completed_step || 0;
          
          // If onboarding is complete, navigate to dashboard
          if (completedStep === 5) {
            navigate("/dashboard");
            return;
          }
          
          // If current step is ahead of completed step + 1, redirect to appropriate step
          const currentStep = 4;
          if (currentStep > completedStep + 1) {
            const nextStep = completedStep + 1;
            navigate(`/profile-step-${nextStep}`);
            return;
          }

          // Determine verification status
          if (profile.is_verification_on_progress) {
            setVerificationStatus("pending");
          } else if (profile.profile_completed_step === 3 && profile.is_verified === true) {
            setVerificationStatus("success");
          } else if (profile.is_verified === false && profile.profile_completed_step === 3) {
            setVerificationStatus("rejected");
            setRejectionNote(profile.rejection_note || "");
            setRejectedStep(profile.rejection_step || null);
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        if (err.code !== 404) {
          const currentLang = localStorage.getItem("app_language") || "en";
          const translations = {
            en: { failedToLoad: "Failed to load profile data" },
            ar: { failedToLoad: "فشل تحميل بيانات الملف الشخصي" },
          };
          showToast(err.message || translations[currentLang].failedToLoad, "error");
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfile();
  }, [USER_BASE_URL, VERSION, navigate]);

  // Refresh verification status
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/profile/`,
        "GET",
        null,
        {}
      );

      if (data && data.response_code === 200 && data.response) {
        const profile = data.response;

        // Determine verification status
        if (profile.is_verification_on_progress) {
          setVerificationStatus("pending");
        } else if (profile.profile_completed_step === 3 && profile.is_verified === true) {
          setVerificationStatus("success");
        } else if (profile.is_verified === false && profile.profile_completed_step === 3) {
          setVerificationStatus("rejected");
          setRejectionNote(profile.rejection_note || "");
          setRejectedStep(profile.rejection_step || null);
        }
      }
    } catch (err) {
      console.error("Error refreshing profile:", err);
      const currentLang = localStorage.getItem("app_language") || "en";
      const translations = {
        en: { somethingWrong: "Something went wrong!" },
        ar: { somethingWrong: "حدث خطأ ما!" },
      };
      showToast(err.message || translations[currentLang].somethingWrong, "error");
    } finally {
      setRefreshing(false);
    }
  };

  // Navigate to rejected step
  const handleGoToErrorPage = () => {
    if (rejectedStep) {
      navigate(`/profile-step-${rejectedStep}`);
    } else {
      // Default to step 1 if no rejected_step is specified
      navigate("/profile-step-1");
    }
  };

  // Handle continue button
  const handleContinue = () => {
    // Navigate to dashboard or next step
    navigate("/profile-step-5");
  };

  if (initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (!verificationStatus) {
    return (
      <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Navbar is_back_arrow={true} is_step_heading={true} heading={t.step} step_number={4} />
        <div className="verification-status-container">
          <p>{t.failedToLoad}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Navbar is_back_arrow={true} is_step_heading={true} heading={t.step} step_number={4} />

      {/* Title */}
      <h2 className="verification-title">
          {verificationStatus === "pending" && t.verificationPending}
          {verificationStatus === "success" && t.verificationCompleted}
          {verificationStatus === "rejected" && t.verificationFailed}
        </h2>

        {/* Message */}
        <p className="verification-message">
          {verificationStatus === "pending" && t.pendingMessage}
          {verificationStatus === "success" && t.successMessage}
          {verificationStatus === "rejected" && t.failedMessage}
        </p>

        {/* Status Image */}
        <div className="verification-image-container">
          {verificationStatus === "pending" && (
            <img src={pendingImage} alt="Pending" className="verification-image" />
          )}
          {verificationStatus === "success" && (
            <img src={successImage} alt="Success" className="verification-image" />
          )}
          {verificationStatus === "rejected" && (
            <img src={rejectedImage} alt="Rejected" className="verification-image" />
          )}
        </div>

        {/* Rejection Note - Only show for rejected status */}
        {verificationStatus === "rejected" && rejectionNote && (
          <div className="failed-info-container">
            <p className="failed-info-title">{t.failedInfo}</p>
            <div className="failed-info-box">
              <p className="failed-info-text">{rejectionNote}</p>
            </div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={
            verificationStatus === "pending"
              ? handleRefresh
              : verificationStatus === "success"
              ? handleContinue
              : handleGoToErrorPage
          }
          className="continue-btn"
          disabled={refreshing}
        >
          {refreshing ? (
            <span className="loader"></span>
          ) : (
            <>
              {verificationStatus === "pending" && t.refreshStatus}
              {verificationStatus === "success" && t.continue}
              {verificationStatus === "rejected" && t.goToErrorPage}
            </>
          )}
        </button>
    </div>
  );
}

